/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 *  @author VJAYAH
 *  10/29/2024
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the cache setup form
 */
class CacheSetupForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            id: null,
            createdDate: '',
            modifiedDate: '',
            host: '',
            url: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Host *',
                    inputName: 'host',
                    placeholder: 'Please enter host name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'URL *',
                    inputName: 'url',
                    placeholder: 'Please enter URL here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const action = this.props.match.params.id;

        // set icons to the header
        if (action === 'create') {
            EventEmitter.emit('setPageHeaderData', {
                page: 'cache-setup',
                pageName: "Cache Setup",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/cache-setup",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'cache-setup',
                pageName: "Cache Setup",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/cache-setup",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteCache);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }


    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {

        const action = this.props.match.params.id;


        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);


        if (action !== 'create') {

            const url = `/cache-setup/${action}`;

            await API.get(url, {
                headers: {Authorization: token},
                cancelToken: this.source.token
            })
                .then(res => {

                    const data = res.data;
                    oldState.id = (data.id !== undefined) ? data.id : null;
                    oldState.createdDate = (data.createdDate !== undefined) ? data.createdDate : '';
                    oldState.modifiedDate = (data.modifiedDate !== undefined) ? data.modifiedDate : '';
                    oldState.host = (data.host !== undefined) ? data.host : '';
                    oldState.url = (data.url !== undefined) ? data.url : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                })
                .catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteCache);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel cache setup async calls");
    };

    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        let data = null;
        if (this.state.id !== null) {
            data = {
                id: this.state.id,
                host: this.state.host,
                url: this.state.url,
                createdDate: this.state.createdDate,
                modifiedDate: new Date().toISOString()
            };
        } else {
            data = {
                host: this.state.host,
                url: this.state.url,
                createdDate: new Date().toISOString(),
                modifiedDate: new Date().toISOString()
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            if (this.state.id !== null) {
                this.updateCache(data);
            } else {
                this.createCache(data);
            }
        }
    };

    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'host':
                if (value === '')
                    error[key] = "Host is Required!";
                else if (value === ' ')
                    error[key] = "Host can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Host can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'url':
                if (value === '')
                    error[key] = "URL is Required!";
                else if (value === ' ')
                    error[key] = "URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "URL can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "URL must begin with http://, https:// or /";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    createCache = async (data) => {
        await API.post('/cache-setup',
            {
                "host": data.host,
                "url": data.url,
                "createdDate": data.createdDate,
                "modifiedDate": data.modifiedDate
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Cache Setup");
            this.props.history.push('/home/cache-setup');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    updateCache = async (data) => {
        const url = `/cache-setup/${data.id}`;
        await API.put(url,
            {
                "id": data.id,
                "host": data.host,
                "url": data.url,
                "createdDate": data.createdDate,
                "modifiedDate": data.modifiedDate
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Cache Setup");
            this.props.history.push('/home/cache-setup');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    displayDeleteWarningMessage = () => {
        if (this.state.url !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'cache-setup',
                name: this.state.host
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Cache Setup",
                alertTitle: "Error"
            });
        }
    };

    deleteCache = async (data) => {
        if (data && (this.props.match.path === "/home/cache-setup/:id")) {
            await API.delete('/cache-setup?id=' + this.state.id, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Cache Setup");
                    this.props.history.push('/home/cache-setup');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Cache Setup already exists!");
        } else if (error.message.includes('409')) {
            this.setAlert("Error", "Sorry", "This URL already exists! Try a new one");
        }
        else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.host = "";
        newState.url = "";
        newState.createdDate = "";
        newState.modifiedDate = "";
        newState.errors = {};

        this.setState(newState);
    };

    navigateToPreviousPage = () => {
        this.props.history.push('/home/cache-setup');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default CacheSetupForm;